// src/pages/LegalDocumentPage.tsx
import React, { useEffect } from "react";
import { Box, Button, Paper, Typography } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { useNavigate } from "react-router-dom";
import {
  getLegalMeta,
  LegalDocType,
  LegalDocumentContent,
} from "../legal/legalDocuments";

const CARD_BG = "#0b1522";
const BORDER = "rgba(120,140,180,.22)";
const TEXT_MAIN = "#eaf2ff";
const TEXT_SUB = "#9fb4d8";
const PRIMARY = "#7fb7ff";

type PublicLegalType = Extract<LegalDocType, "terms" | "privacy">;

interface LegalDocumentPageProps {
  type: PublicLegalType;
}

export default function LegalDocumentPage({ type }: LegalDocumentPageProps) {
  const navigate = useNavigate();
  const meta = getLegalMeta(type);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${meta.title} | PolicyIntel`;

    return () => {
      document.title = previousTitle;
    };
  }, [meta.title]);

  return (
    <Box
      component="main"
      sx={{
        width: "100%",
        maxWidth: 980,
        mx: "auto",
        py: { xs: 1, sm: 2, md: 3 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          overflow: "hidden",
          border: `1px solid ${BORDER}`,
          borderRadius: { xs: 2, md: 3 },
          bgcolor: CARD_BG,
          color: TEXT_MAIN,
          boxShadow: "0 18px 44px rgba(4,12,24,.20)",
        }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 3, md: 4 },
            pt: { xs: 2, sm: 3 },
            pb: 2,
            borderBottom: `1px solid ${BORDER}`,
          }}
        >
          <Typography
            component="div"
            sx={{
              color: PRIMARY,
              fontSize: 12,
              fontWeight: 900,
              letterSpacing: ".12em",
            }}
          >
            POLICYINTEL LEGAL
          </Typography>

          <Typography
            component="h1"
            sx={{
              mt: 0.8,
              color: TEXT_MAIN,
              fontSize: { xs: 25, sm: 31 },
              fontWeight: 950,
              letterSpacing: "-0.035em",
            }}
          >
            {meta.title}
          </Typography>

          <Typography
            sx={{
              mt: 0.75,
              color: TEXT_SUB,
              fontSize: { xs: 12, sm: 13 },
              lineHeight: 1.7,
              wordBreak: "keep-all",
            }}
          >
            PolicyIntel 서비스의 {meta.title}을 공개 페이지에서 확인할 수 있습니다.
            회원가입 여부와 관계없이 이 페이지에 직접 접근할 수 있습니다.
          </Typography>
        </Box>

        <Box
          sx={{
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 2.5, sm: 3.5 },
            "& > div > .MuiTypography-root:first-of-type": {
              // LegalDocumentContent 내부 제목은 상단 public header와 중복되므로 시각적 위계만 낮춘다.
              fontSize: 16,
            },
          }}
        >
          <LegalDocumentContent type={type} />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
            px: { xs: 2, sm: 3, md: 4 },
            py: 2,
            borderTop: `1px solid ${BORDER}`,
          }}
        >
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate("/")}
            sx={{
              color: TEXT_MAIN,
              fontWeight: 900,
              borderRadius: 2,
            }}
          >
            PolicyIntel 홈
          </Button>

          <Box sx={{ flex: 1 }} />

          {type !== "terms" && (
            <Button
              onClick={() => navigate("/terms")}
              sx={{ color: PRIMARY, fontWeight: 850 }}
            >
              이용약관
            </Button>
          )}

          {type !== "privacy" && (
            <Button
              onClick={() => navigate("/privacy")}
              sx={{ color: PRIMARY, fontWeight: 850 }}
            >
              개인정보처리방침
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
